import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowUp, FaShoppingBag } from 'react-icons/fa';

function Footer() {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <footer className="bg-navy-900 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* About */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.png" alt="Marple Swimming Club" className="h-12 w-auto" />
                <span className="font-display text-lg font-bold">Marple Swimming Club</span>
              </div>
              <p className="text-navy-200 leading-relaxed max-w-md">
                Training and welcoming swimmers of every age and ability since 1933 &mdash;
                from first strokes to competitive racing.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-display font-semibold text-white mb-4 uppercase tracking-wide text-sm">
                Quick Links
              </h3>
              <ul className="space-y-2 text-navy-200">
                <li><Link to="/about" className="hover:text-primary-300 transition-colors">About The Club</Link></li>
                <li><Link to="/programs" className="hover:text-primary-300 transition-colors">Programs</Link></li>
                <li><Link to="/events" className="hover:text-primary-300 transition-colors">Events</Link></li>
                <li><Link to="/results" className="hover:text-primary-300 transition-colors">Results</Link></li>
                <li><Link to="/new-pool" className="hover:text-primary-300 transition-colors">New Pool Updates</Link></li>
              </ul>
            </div>

            {/* Get In Touch */}
            <div>
              <h3 className="font-display font-semibold text-white mb-4 uppercase tracking-wide text-sm">
                Get In Touch
              </h3>
              <ul className="space-y-2 text-navy-200">
                <li><Link to="/contact" className="hover:text-primary-300 transition-colors">Contact &amp; Enquiries</Link></li>
                <li><Link to="/contact#welfare-officer" className="hover:text-primary-300 transition-colors">Welfare Officer</Link></li>
                <li>
                  <a
                    href="https://pbteamwear.co.uk/collections/marple-sc"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 hover:text-primary-300 transition-colors"
                  >
                    <FaShoppingBag className="w-3.5 h-3.5" />
                    Club Shop
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-navy-300">
            <p>&copy; {new Date().getFullYear()} Marple Swimming Club. All rights reserved.</p>
            <p className="italic">Making waves in our community since 1933</p>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 bg-primary-600 text-white p-4 rounded-full shadow-2xl hover:bg-primary-700 transition-colors z-50 group"
            aria-label="Back to top"
          >
            <FaArrowUp className="w-6 h-6 group-hover:animate-bounce" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}

export default Footer;
